import { QueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { AdminApi } from 'api';
import type { Admin } from 'api/methods/AdminApi';

/**
 * [Admin] Query Key 생성
 * --
 */
export const adminQueryKeys = createQueryKeys('admin', {
  getAdmin: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getAdmins: (params?: { [key: string]: string | number }) => [params],
  getShopAdmins: (params?: { [key: string]: string | number }) => [
    'shop_admins',
    params,
  ],
  getStoreAdmins: (params?: { [key: string]: string | number }) => [
    'store_admins',
    params,
  ],
});

/**
 * [Admin] 관리자 전체 조회
 * --
 */
export const useGetAdmins = (
  params?: { [key: string]: string | number },
  options?: QueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: adminQueryKeys.getAdmins(params).queryKey,
    queryFn: () => AdminApi.getAdmins(params),
    ...options,
  });

  return data;
};

/**
 * [Admin] 쇼핑몰 관리자 전체 조회
 * --
 */
export const useGetShopAdmins = (params?: {
  [key: string]: string | number;
}) => {
  const data = useQuery({
    queryKey: adminQueryKeys.getShopAdmins(params).queryKey,
    queryFn: () => AdminApi.getAdmins(params),
    select: (result) => {
      const { status, message, data } = result;

      const shopAdmins = data?.filter(
        (item: Admin) => item.type === 'SHOP_ADMIN'
      );

      return {
        status,
        message,
        data: shopAdmins,
      };
    },
  });

  return data;
};

/**
 * [Admin] 매장 관리자 전체 조회
 * --
 */
export const useGetStoreAdmins = (params?: {
  [key: string]: string | number;
}) => {
  const data = useQuery({
    queryKey: adminQueryKeys.getStoreAdmins(params).queryKey,
    queryFn: () => AdminApi.getAdmins(params),
    select: (result) => {
      const { status, message, data } = result;

      const storeAdmins = data?.filter(
        (item: Admin) => item.type === 'STORE_ADMIN'
      );

      return {
        status,
        message,
        data: storeAdmins,
      };
    },
  });

  return data;
};

/**
 * [Admin] 관리자 상세 조회
 * --
 */
export const useGetAdmin = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: adminQueryKeys.getAdmin(idx, params).queryKey,
    queryFn: () => AdminApi.getAdmin(idx, params),
  });

  return data;
};

/**
 * [Admin] 관리자 생성
 * --
 */
export const useCreateAdmin = () => {
  const { mutateAsync: createAdmin, ...result } = useMutation({
    mutationFn: (variables: Admin) => AdminApi.createAdmin(variables),
  });

  return { createAdmin, ...result };
};

/**
 * [Admin] 관리자 수정
 * --
 */
export const useUpdateAdmin = () => {
  const { mutateAsync: updateAdmin, ...result } = useMutation({
    mutationFn: (variables: Admin) => AdminApi.updateAdmin(variables),
  });

  return { updateAdmin, ...result };
};

/**
 * [Admin] 관리자 삭제
 * --
 */
export const useDeleteAdmin = () => {
  const { mutateAsync: deleteAdmin, ...result } = useMutation({
    mutationFn: (variables: number) => AdminApi.deleteAdmin(variables),
  });

  return { deleteAdmin, ...result };
};
